import React, { useState } from 'react';

/**
 * Send a payment reminder to the customer via email and/or WhatsApp.
 * Props: row, onClose(), onSubmit(channels[]) -> {success, data:{results:[...]}, error}
 */
export default function SendReminderModal({ row, onClose, onSubmit }) {
  // Default each channel to ON only where there is somewhere to send it. The
  // checkboxes are DISABLED when the contact is missing, so defaulting to true
  // left the operator unable to untick it — the reminder went out on a channel
  // with no destination, and the "pick at least one channel" guard could never fire.
  const [email, setEmail] = useState(Boolean(row?.customer_email));
  const [whatsapp, setWhatsapp] = useState(Boolean(row?.customer_phone));
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    const channels = [email && 'email', whatsapp && 'whatsapp'].filter(Boolean);
    if (channels.length === 0) { setErr('Pick at least one channel'); return; }
    setSubmitting(true);
    const res = await onSubmit(channels);
    setSubmitting(false);
    if (res && res.success === false) { setErr(res.error || 'Failed to send reminder'); return; }
    setResults(res?.data?.results || []);
  };

  const badge = (status) => ({
    sent: 'bg-green-100 text-green-700',
    skipped: 'bg-yellow-100 text-yellow-700',
    rate_limited: 'bg-orange-100 text-orange-700',
    failed: 'bg-red-100 text-red-700',
  }[status] || 'bg-gray-100 text-gray-700');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Send Payment Reminder</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="text-gray-600">
            To <span className="font-semibold">{row?.customer_name || 'customer'}</span> for challan{' '}
            <span className="font-semibold">{row?.challan_number}</span>.
          </div>
          <div className="text-xs text-gray-500">
            Email: {row?.customer_email || '—'} · Phone: {row?.customer_phone || '—'}
          </div>
          {!results && (
            <form onSubmit={submit} className="space-y-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} disabled={!row?.customer_email} />
                <span>Email {row?.customer_email ? '' : '(no email on file)'}</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} disabled={!row?.customer_phone} />
                <span>WhatsApp {row?.customer_phone ? '' : '(no phone on file)'}</span>
              </label>
              {err && <p className="text-red-600 text-xs">{err}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border-2 border-gray-300">Cancel</button>
                <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold disabled:opacity-60">
                  {submitting ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
          )}
          {results && (
            <div className="space-y-2">
              {results.map((r) => (
                <div key={r.channel} className="flex items-center justify-between">
                  <span className="capitalize">{r.channel}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badge(r.status)}`}>
                    {r.status}{r.detail ? ` — ${r.detail}` : ''}
                  </span>
                </div>
              ))}
              <div className="flex justify-end pt-2">
                <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-800 text-white">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
